import type * as React from 'react';
import { type Control, type SubmitHandler, useController, useForm } from 'react-hook-form';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import * as hiveCore from '@gitbook/hive-core';
import { useHiveBranch } from '@gitbook/hive-react';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    DialogRoot,
    FieldError,
    FieldGroup,
    Fieldset,
    Form,
    Heading,
    Label,
    Paper,
    Popover,
    PopoverRoot,
    PopoverTrigger,
    Submit,
    TextField,
    TextInput,
    TextInputTextPrefix,
    useDialogAction,
} from '@gitbook/spine-react';
import { assertIsDefined } from '@gitbook/util-ts';
import { normalizeURLSlug, validateURLSlug } from '@gitbook/util-url';

import { ContentReferenceInput, PageIcon, PageIconPicker } from '../../../Components';
import { contentRefFieldDescription, contentRefFieldPlaceholder } from './InsertLinkModal';

type EditPageFormInputs = {
    title: string;
    iconEmoji: hiveCore.RevisionPageIconProps;
    slug: string;
    contentRef: api.ContentRef | null;
};

/**
 * Dialog to edit a page.
 */
export function EditPageDialog(props: {
    revision: hiveCore.Revision;
    page: hiveCore.RevisionPage;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
    onClose: () => void;
    isOpen: boolean;
}): React.ReactElement | null {
    const { revision, analyticsContext, page, onClose, isOpen = false } = props;

    return (
        <DialogRoot
            isOpen={isOpen}
            onOpenChange={(isOpen) => {
                if (!isOpen) {
                    onClose();
                }
            }}
        >
            <Dialog>
                <PageEditForm page={page} revision={revision} analyticsContext={analyticsContext} />
            </Dialog>
        </DialogRoot>
    );
}

function PageEditForm(props: {
    page: hiveCore.RevisionPage;
    revision: hiveCore.Revision;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
}): React.ReactElement {
    const { page, revision, analyticsContext } = props;

    const [isPending, startDialogAction] = useDialogAction();

    const form = useForm<EditPageFormInputs>({
        defaultValues: {
            title: page.title,
            iconEmoji: { emoji: page.emoji, icon: page.icon },
            slug: 'slug' in page && page.slug ? page.slug : '',
            contentRef:
                page.type === hiveCore.RevisionPageType.Link
                    ? (page.target as api.ContentRef)
                    : null,
        },
    });

    const hiveBranch = useHiveBranch();

    const title = form.watch('title');
    const iconEmoji = form.watch('iconEmoji');

    // Detect prefix for page slub
    const pageParent = hiveCore.getPageParentInRevision(revision, page.id);
    const pageParentPath = pageParent
        ? hiveCore.getAbsolutePagePathInRevision(revision, pageParent.id)
        : '';
    const pageCurrentPath =
        page.type !== hiveCore.RevisionPageType.Link
            ? hiveCore.getRelativePagePathInRevision({
                  revision,
                  pageId: page.id,
                  newTitle: normalizeURLSlug(title),
              })
            : '';

    const onSubmit: SubmitHandler<EditPageFormInputs> = async ({
        title,
        iconEmoji,
        slug,
        contentRef,
    }) => {
        await startDialogAction(async ({ close }) => {
            assertIsDefined(hiveBranch);

            // IMPORTANT!!
            // We normalize the slug here so that the Hive worker doesn't have to
            // do slug normalization because it negatively impacts performance.
            const update =
                page.type !== hiveCore.RevisionPageType.Link
                    ? {
                          title,
                          slug: slug ? normalizeURLSlug(slug) : undefined,
                      }
                    : {
                          title,
                          target: contentRef,
                      };

            await hiveBranch.change((change) => {
                change.updatePage(page.id, update);
                if (iconEmoji.emoji !== page.emoji || iconEmoji.icon !== page.icon) {
                    change.setPageIcon(page.id, iconEmoji);
                }
            });

            if (page.title !== title) {
                analytics.logEvent({
                    ...analyticsContext,
                    type: 'edit_page_title',
                    pageKind: page.type,
                });
            }
            if (hiveCore.isRevisionPageParent(page) && slug && page.slug !== slug) {
                analytics.logEvent({
                    ...analyticsContext,
                    type: 'edit_page_path',
                    pageKind: page.type,
                });
            }
            if (page.emoji !== iconEmoji.emoji && page.icon !== iconEmoji.icon) {
                if (iconEmoji.emoji) {
                    analytics.logEvent({
                        ...analyticsContext,
                        type: 'edit_pick_page_emoji',
                        pageKind: page.type,
                        emoji: iconEmoji.emoji,
                    });
                } else if (iconEmoji.icon) {
                    analytics.logEvent({
                        ...analyticsContext,
                        type: 'edit_pick_page_icon',
                        pageKind: page.type,
                        icon: iconEmoji.icon,
                    });
                } else if (page.emoji) {
                    analytics.logEvent({
                        ...analyticsContext,
                        type: 'edit_remove_page_emoji',
                        pageKind: page.type,
                    });
                } else {
                    analytics.logEvent({
                        ...analyticsContext,
                        type: 'edit_remove_page_icon',
                        pageKind: page.type,
                    });
                }
            }

            await close();
        });
    };

    const pageType = page.type === hiveCore.RevisionPageType.Document ? 'page' : page.type;

    return (
        <Form aria-labelledby="form-edit-page" form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogHeader>
                <Heading id="form-edit-page">Edit {pageType}</Heading>
            </DialogHeader>

            <DialogBody>
                <Fieldset>
                    <TextField name="title" required className="mb-6">
                        <FieldGroup>
                            <Label>Title</Label>
                            <Description>
                                Describe the {pageType} content for your readers and also improved
                                SEO
                            </Description>
                            <div className="mt-2 flex items-end gap-2">
                                <PopoverRoot>
                                    <PopoverTrigger>
                                        <Button
                                            kind="secondary"
                                            size="large"
                                            iconOnly
                                            leadingIcon={
                                                iconEmoji.emoji || iconEmoji.icon ? (
                                                    <PageIcon page={iconEmoji} size={14} />
                                                ) : (
                                                    Icon.EmojiReaction
                                                )
                                            }
                                            label="Pick icon"
                                            className="size-9 min-w-9"
                                            disabled={isPending}
                                        />
                                    </PopoverTrigger>

                                    <Popover>
                                        <Paper>
                                            <PageIconPickerField control={form.control} />
                                        </Paper>
                                    </Popover>
                                </PopoverRoot>

                                <TextInput
                                    size="large"
                                    className="flex-1"
                                    disabled={isPending}
                                    autoFocus
                                />
                                <FieldError />
                            </div>
                        </FieldGroup>
                    </TextField>
                    {page.type === hiveCore.RevisionPageType.Link ? (
                        <ContentRefField control={form.control} disabled={isPending} />
                    ) : page.type === hiveCore.RevisionPageType.Document ||
                      page.type === hiveCore.RevisionPageType.Group ? (
                        <SlugField
                            control={form.control}
                            pageParentPath={pageParentPath}
                            pageCurrentPath={pageCurrentPath}
                            disabled={isPending}
                        />
                    ) : null}
                </Fieldset>
            </DialogBody>

            <DialogFooter>
                <DialogCloseButton />
                <Submit label="Save" />
            </DialogFooter>
        </Form>
    );
}

function PageIconPickerField(props: { control: Control<EditPageFormInputs> }) {
    const { control } = props;

    const { field } = useController({ name: 'iconEmoji', control });

    return <PageIconPicker value={field.value} onChange={field.onChange} />;
}

function ContentRefField(props: { control: Control<EditPageFormInputs>; disabled: boolean }) {
    const { control, disabled } = props;

    const { field } = useController({ name: 'contentRef', control, rules: { required: true } });

    return (
        <ContentReferenceInput
            label="URL or content"
            size="large"
            labelDescription={contentRefFieldDescription}
            placeholder={contentRefFieldPlaceholder}
            value={field.value}
            onChangeValue={field.onChange}
            disabled={disabled}
        />
    );
}

function SlugField(props: {
    control: Control<EditPageFormInputs>;
    pageParentPath: string;
    pageCurrentPath: string;
    disabled: boolean;
}) {
    const { control, pageParentPath, pageCurrentPath, disabled } = props;

    const { field, fieldState } = useController({
        name: 'slug',
        control,
        rules: {
            validate: (value) => {
                return value ? validateURLSlug(value) : true;
            },
        },
    });

    return (
        <>
            <Label>Slug</Label>
            <TextInput
                size="large"
                prefix={<TextInputTextPrefix text={`${pageParentPath}/`} />}
                value={field.value}
                placeholder={pageCurrentPath}
                onChange={(event) => field.onChange(event.target.value)}
                ref={field.ref}
                onBlur={field.onBlur}
                disabled={disabled}
                feedback={
                    fieldState.error?.type === 'validate'
                        ? {
                              type: 'warning',
                              message: (
                                  <>
                                      The page slug contains unsupported characters for an URL.
                                      Saving will replace it by{' '}
                                      <span className="font-bold">
                                          {normalizeURLSlug(field.value)}
                                      </span>
                                      .
                                  </>
                              ),
                          }
                        : undefined
                }
            />
        </>
    );
}
